/**
 * When to drop the station connection.
 *
 * The bridge is cheap but not free: while a browser is attached the station
 * polls the API four times faster, so a tab left open on a back-office machine
 * would keep a bench "active" all day. Letting go when nobody is there hands
 * that back, and releases the scale for any other tab that wants it.
 *
 * The policy is deliberately graded rather than a single timeout, because the
 * three states mean quite different things about whether someone is working.
 * @module bridge-suspend
 */

/**
 * Tab is in the background — nobody can see a weight even if one arrives.
 *
 * Short, but not instant: flicking to another tab to check an order and coming
 * straight back shouldn't tear the connection down and build it again.
 */
export const HIDDEN_GRACE_MS = 30_000;

/**
 * Visible but not focused — the operator may well be looking at the screen
 * while typing somewhere else, so this gets considerably longer than hidden.
 */
export const BLURRED_GRACE_MS = 120_000;

/**
 * Focused and visible, but nothing has happened for a while.
 *
 * Generous, because being wrong here is the costly direction: an operator who
 * sets a package down after a quiet spell should get a weight, not a
 * reconnect. Scale readings count as activity, so someone weighing steadily
 * without touching the keyboard never reaches this.
 */
export const IDLE_MS = 900_000;

export interface BridgeActivityState {
  /** `document.hidden` — the tab is in the background. */
  hidden: boolean;
  /** `document.hasFocus()` — this window has keyboard focus. */
  focused: boolean;
}

/**
 * How long to wait before dropping the connection, given the page's state.
 * @param state - See {@link BridgeActivityState}.
 * @returns Delay in milliseconds from the last activity.
 */
export function bridgeSuspendDelay(state: BridgeActivityState): number {
  if (state.hidden) return HIDDEN_GRACE_MS;
  if (!state.focused) return BLURRED_GRACE_MS;
  return IDLE_MS;
}
